import {
  Box,
  Flex,
  Progress,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToken,
} from "@chakra-ui/react";
import { Duration, Instant, LocalDate } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@uirouter/react";
import React from "react";
import SingleDatePicker from "../../shared/components/DatePicker/SingleDatePicker";
import Page from "../../shared/components/Page";
import Select from "../../shared/components/Select";
import useApi from "../../shared/hooks/useApi";
import { ResponseOf } from "../../core/api";
import { OfficeId } from "../../shared/schema/schema";
import { dateFormatter } from "../../shared/utils/date-formatter";
import { formatErrorResponse } from "../../shared/utils/format-response-error";
import {
  BarsByColor,
  DashboardCard,
  Donut,
  GroupedBars,
  MetricTile,
} from "./components/dashboard/DashboardParts";
import { Priority } from "./dailyNotesShared";

/** The statistics open on the last month of visits. The backlog ignores the window. */
const DEFAULT_WINDOW_DAYS = 30;

type DashboardResponse = ResponseOf<"post", "./visit_daily_notes/dashboard">;

const PRIORITY_LABEL: Record<Priority, string> = {
  URGENT: "Urgent",
  HIGH: "High",
  ROUTINE: "Non-concerning",
};

function percent(part: number, whole: number): string {
  return whole === 0 ? "0%" : `${Math.round((part / whole) * 100)}%`;
}

/** Hours while under two days, where a co-signature usually lands; days beyond that. */
function formatHours(hours: number): string {
  return hours < 48 ? `${Math.round(hours)} h` : `${Math.round((hours / 24) * 10) / 10} days`;
}

/**
 * The overview a nurse or manager lands on.
 *
 * Two halves with different clocks. The backlog -- what is waiting for a signature right now --
 * is always current and every number in it opens the Daily notes list with the matching filters,
 * so a figure here is a doorway to the notes behind it. The statistics describe a window of visit
 * dates and are only read, never clicked: they answer "how are we doing", not "what do I do next".
 *
 * Every figure comes from one request, so the tiles and charts cannot disagree with each other.
 */
export default function DailyNoteDashboardTab() {
  const { queries } = useApi();
  const { stateService } = useRouter();

  const offices = useQuery(queries.dailyNotes.reviewOffices());

  const [officeId, setOfficeId] = React.useState<OfficeId | null>(null);
  const [visitFrom, setVisitFrom] = React.useState<LocalDate | null>(() =>
    LocalDate.now().minusDays(DEFAULT_WINDOW_DAYS)
  );
  const [visitTo, setVisitTo] = React.useState<LocalDate | null>(() => LocalDate.now());

  // Stable between renders: the query key is built from it.
  const filters = React.useMemo(
    () => ({ officeId, visitFrom, visitTo }),
    [officeId, visitFrom, visitTo]
  );

  const query = useQuery(queries.dailyNotes.dashboard(filters));

  const [red, orange, gray, lightGray, blue, purple] = useToken("colors", [
    "red.500",
    "orange.400",
    "gray.500",
    "gray.300",
    "blue.400",
    "purple.400",
  ]);

  // A tile opens the patients list with filters, scoped to the same office the tile counted.
  const openList = (filter: Record<string, unknown>) =>
    stateService.go("app.visits.dailyNotes", {
      notesPatient: null,
      notesView: null,
      notesOffice: officeId,
      ...filter,
    });

  // A bar covers a window of visit dates. Whole days, counted back from today.
  const openAgeBucket = (key: string) => {
    const today = LocalDate.now();

    switch (key) {
      case "upTo7":
        return openList({ notesFrom: today.minusDays(7).toString(), notesTo: today.toString() });
      case "8to14":
        return openList({
          notesFrom: today.minusDays(14).toString(),
          notesTo: today.minusDays(8).toString(),
        });
      case "15to30":
        return openList({
          notesFrom: today.minusDays(30).toString(),
          notesTo: today.minusDays(15).toString(),
        });
      default:
        return openList({ notesTo: today.minusDays(31).toString(), notesAnyDate: true });
    }
  };

  // Every co-signature tile opens the same queue: the Attestations tab, waiting batches only,
  // for the office the tile counted.
  const openAwaitingCosignature = () =>
    stateService.go("app.visits.dailyNoteAttestations", {
      attestationsStatus: "AWAITING_COSIGNATURE",
      attestationsOffice: officeId,
    });

  const data = query.data;

  return (
    <Box>
      <Page.Header pb={2}>
        <Page.Title>Dashboard</Page.Title>
      </Page.Header>

      <Page.Content pt={1}>
        <Flex gap={3} mb={5} wrap="wrap">
          <Select
            allowUnselect={true}
            isLoading={offices.isPending}
            label="Office"
            multiple={false}
            options={(offices.data ?? []).map((office) => ({
              label: office.name,
              value: office.id,
            }))}
            value={officeId}
            onChange={(x) => setOfficeId(x ?? null)}
          />
          <SingleDatePicker
            placeholderText="Visits from"
            selected={visitFrom}
            onChange={setVisitFrom}
          />
          <SingleDatePicker placeholderText="Visits to" selected={visitTo} onChange={setVisitTo} />
        </Flex>

        {query.isError ? (
          <Text color="red.700">{formatErrorResponse(query.error)}</Text>
        ) : data === undefined ? (
          <Flex justify="center" py={12}>
            <Spinner color="blue.500" size="lg" thickness="3px" />
          </Flex>
        ) : (
          <Flex direction="column" gap={6}>
            <Box>
              <SectionTitle>Waiting for review</SectionTitle>
              <SectionNote>
                What is unsigned right now, whatever the dates above. Click a number to open the
                notes.
              </SectionNote>

              <SimpleGrid columns={{ base: 2, lg: 4 }} gap={3}>
                <MetricTile
                  hint={`across ${data.backlog.patientCount} patients`}
                  label="Notes to review"
                  value={data.backlog.total}
                  onClick={() => openList({ notesAnyDate: true })}
                />
                <MetricTile
                  hint={percent(data.backlog.due, data.backlog.total) + " of the backlog"}
                  label="Due"
                  value={data.backlog.due}
                  onClick={() => openList({ notesDue: true })}
                />
                <MetricTile
                  hint={percent(data.backlog.flagged, data.backlog.total) + " of the backlog"}
                  label="Concerning"
                  value={data.backlog.flagged}
                  onClick={() => openList({ notesFlagged: true, notesAnyDate: true })}
                />
                <MetricTile
                  hint="review these first"
                  label="Due and concerning"
                  tone={data.backlog.dueAndFlagged > 0 ? "danger" : "default"}
                  value={data.backlog.dueAndFlagged}
                  onClick={() => openList({ notesDue: true, notesFlagged: true })}
                />
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, lg: 2 }} gap={3} mt={3}>
                <DashboardCard title="To review by priority">
                  <Donut
                    slices={[
                      { key: "URGENT", label: "Urgent", value: data.backlog.urgent, color: red },
                      { key: "HIGH", label: "High", value: data.backlog.high, color: orange },
                      {
                        key: "ROUTINE",
                        label: "Non-concerning",
                        value: data.backlog.routine,
                        color: gray,
                      },
                      {
                        key: "UNCLASSIFIED",
                        label: "Analysing",
                        value: data.backlog.unclassified,
                        color: lightGray,
                      },
                    ]}
                  />
                </DashboardCard>
                <DashboardCard hint="click a bar" title="How long notes have waited">
                  <BarsByColor
                    bars={[
                      {
                        key: "upTo7",
                        label: "0-7 days",
                        value: data.backlog.ageUpTo7Days,
                        color: blue,
                      },
                      {
                        key: "8to14",
                        label: "8-14 days",
                        value: data.backlog.age8To14Days,
                        color: blue,
                      },
                      {
                        key: "15to30",
                        label: "15-30 days",
                        value: data.backlog.age15To30Days,
                        color: red,
                      },
                      {
                        key: "over30",
                        label: "Over 30 days",
                        value: data.backlog.ageOver30Days,
                        color: red,
                      },
                    ]}
                    onBarClick={openAgeBucket}
                  />
                </DashboardCard>
              </SimpleGrid>
            </Box>

            {data.cosign === null ? null : (
              <Box>
                <SectionTitle>Waiting for co-signature</SectionTitle>
                <SectionNote>
                  Signed batches a second reviewer has not co-signed yet, whatever the dates above.
                  Their notes go to DoCare only once co-signed. Click a number to open them.
                </SectionNote>

                <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
                  <MetricTile
                    hint={`${data.cosign.awaitingNotes} notes held from DoCare`}
                    label="Batches waiting"
                    value={data.cosign.awaitingBatches}
                    onClick={openAwaitingCosignature}
                  />
                  <MetricTile
                    hint="co-sign these first"
                    label="Concerning batches waiting"
                    tone={data.cosign.awaitingConcerningBatches > 0 ? "danger" : "default"}
                    value={data.cosign.awaitingConcerningBatches}
                    onClick={openAwaitingCosignature}
                  />
                  <MetricTile
                    hint={
                      data.cosign.oldestAwaitingSignedAt === null
                        ? "nothing waiting"
                        : `signed ${dateFormatter.toDateTime(data.cosign.oldestAwaitingSignedAt)}`
                    }
                    label="Oldest waiting"
                    value={
                      data.cosign.oldestAwaitingSignedAt === null
                        ? "—"
                        : formatHours(
                            Duration.between(
                              data.cosign.oldestAwaitingSignedAt,
                              Instant.now()
                            ).toMinutes() / 60
                          )
                    }
                    onClick={openAwaitingCosignature}
                  />
                </SimpleGrid>
              </Box>
            )}

            <Box>
              <SectionTitle>Sync issues</SectionTitle>
              <SectionNote>
                Notes that did not reach DoCare, and incidents Salesforce did not take.
              </SectionNote>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                <MetricTile
                  hint={
                    data.syncIssues.docareFailed === 0 ? "nothing to resend" : "open and resend"
                  }
                  label="DoCare uploads failed"
                  tone={data.syncIssues.docareFailed > 0 ? "danger" : "default"}
                  value={data.syncIssues.docareFailed}
                  onClick={() => stateService.go("app.visits.dailyNoteSyncIssues")}
                />
                <MetricTile
                  hint={
                    data.syncIssues.salesforceNeedsReview > 0
                      ? `${data.syncIssues.salesforceNeedsReview} sent back for review`
                      : data.syncIssues.salesforceFailed === 0
                      ? "nothing to resend"
                      : "open and resend"
                  }
                  label="Salesforce incidents failed"
                  tone={
                    data.syncIssues.salesforceFailed + data.syncIssues.salesforceNeedsReview > 0
                      ? "danger"
                      : "default"
                  }
                  value={data.syncIssues.salesforceFailed + data.syncIssues.salesforceNeedsReview}
                  onClick={() => stateService.go("app.visits.dailyNoteSyncIssues")}
                />
              </SimpleGrid>
            </Box>

            <Box>
              <SectionTitle>Statistics for the visits above</SectionTitle>

              <SimpleGrid columns={{ base: 1, lg: 2 }} gap={3}>
                <DashboardCard title="Compliance: signed within the office's due time">
                  {data.compliance.compliancePct === null ? (
                    <Text color="gray.500" fontSize="sm">
                      No note in these dates has reached its deadline yet.
                    </Text>
                  ) : (
                    <>
                      <Flex align="baseline" gap={2}>
                        <Text fontSize="4xl" fontWeight="semibold">
                          {data.compliance.compliancePct}%
                        </Text>
                        <Text color="gray.600" fontSize="sm">
                          {data.compliance.signedOnTime} of{" "}
                          {data.compliance.signedOnTime +
                            data.compliance.signedLate +
                            data.compliance.overdueUnsigned}{" "}
                          notes
                        </Text>
                      </Flex>
                      <Progress
                        borderRadius="full"
                        colorScheme="green"
                        my={2}
                        size="sm"
                        value={data.compliance.compliancePct}
                      />
                      <Text color="gray.600" fontSize="sm">
                        On time {data.compliance.signedOnTime} · Late {data.compliance.signedLate} ·
                        Overdue, unsigned {data.compliance.overdueUnsigned}
                      </Text>
                    </>
                  )}
                  <Text color="gray.500" fontSize="xs" mt={2}>
                    {data.compliance.goLiveDate === null
                      ? "Counts visits from the day the office went live."
                      : `Counts visits from ${dateFormatter.toDate(
                          data.compliance.goLiveDate
                        )}, when the office went live.`}{" "}
                    {data.compliance.excludedBeforeGoLive > 0
                      ? `${data.compliance.excludedBeforeGoLive} older notes (backfill) are left out. `
                      : ""}
                    Notes still inside their due time are not counted.
                  </Text>
                </DashboardCard>

                <DashboardCard title="Signed notes: the nurse's ruling">
                  <Donut
                    height={130}
                    slices={[
                      {
                        key: "ROUTINE",
                        label: "Non-concerning",
                        value: data.incidents.signedRoutine,
                        color: gray,
                      },
                      {
                        key: "NON_ROUTINE",
                        label: "Concerning",
                        value: data.incidents.signedNonRoutine,
                        color: orange,
                      },
                    ]}
                  />
                  <Text color="gray.600" fontSize="sm" mt={1}>
                    Incidents: {data.incidents.createdInSalesforce + data.incidents.resolved}{" "}
                    created in Salesforce · {data.incidents.sending} sending ·{" "}
                    {data.incidents.failed + data.incidents.needsReview} failed or sent back
                  </Text>
                </DashboardCard>

                <DashboardCard title="Days from visit to signature">
                  <GroupedBars
                    firstColor={blue}
                    firstName="Median"
                    rows={data.timeToSign.map((row) => ({
                      label: PRIORITY_LABEL[row.priority],
                      first: row.medianDays,
                      second: row.slowest10PercentDays,
                    }))}
                    secondColor={purple}
                    secondName="Slowest 10%"
                  />
                </DashboardCard>

                {data.cosign === null ? null : (
                  <DashboardCard title="Time from signature to co-signature">
                    <Flex gap={8}>
                      <Box>
                        <Text color="gray.600" fontSize="sm">
                          Median
                        </Text>
                        <Text fontSize="2xl" fontWeight="semibold">
                          {data.cosign.medianHoursToCosign === null
                            ? "—"
                            : formatHours(data.cosign.medianHoursToCosign)}
                        </Text>
                      </Box>
                      <Box>
                        <Text color="gray.600" fontSize="sm">
                          Slowest 10%
                        </Text>
                        <Text fontSize="2xl" fontWeight="semibold">
                          {data.cosign.slowest10PercentHoursToCosign === null
                            ? "—"
                            : formatHours(data.cosign.slowest10PercentHoursToCosign)}
                        </Text>
                      </Box>
                    </Flex>
                    <Text color="gray.600" fontSize="sm" mt={2}>
                      Co-signed {data.cosign.cosignedInWindow} · still waiting{" "}
                      {data.cosign.awaitingInWindow}
                    </Text>
                  </DashboardCard>
                )}

                <DashboardCard title="AI quality">
                  <AiQuality aiQuality={data.aiQuality} />
                </DashboardCard>
              </SimpleGrid>
            </Box>
          </Flex>
        )}
      </Page.Content>
    </Box>
  );
}

function SectionTitle(props: { children: React.ReactNode }) {
  return (
    <Text color="gray.800" fontSize="md" fontWeight="semibold" mb={1}>
      {props.children}
    </Text>
  );
}

function SectionNote(props: { children: React.ReactNode }) {
  return (
    <Text color="gray.500" fontSize="sm" mb={3}>
      {props.children}
    </Text>
  );
}

/**
 * How the AI's priority compares with what the nurse ruled, and how much work triage took.
 *
 * Two rows by two columns is enough: the AI called it serious or not, the nurse agreed or not.
 * The figures that matter are the off-diagonal ones -- serious notes the nurse called non-concerning
 * are over-flagging, and non-concerning ones she called concerning are misses.
 */
function AiQuality(props: { aiQuality: DashboardResponse["aiQuality"] }) {
  const { aiQuality } = props;

  const count = (serious: boolean, determination: "ROUTINE" | "NON_ROUTINE") =>
    aiQuality.rulings
      .filter(
        (ruling) =>
          ruling.determination === determination &&
          (ruling.priority === "HIGH" || ruling.priority === "URGENT") === serious
      )
      .reduce((sum, ruling) => sum + ruling.count, 0);

  return (
    <Box>
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th pl={0}>AI priority</Th>
            <Th isNumeric={true}>Nurse: non-concerning</Th>
            <Th isNumeric={true}>Nurse: concerning</Th>
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Td pl={0}>Urgent or high</Td>
            <Td isNumeric={true}>{count(true, "ROUTINE")}</Td>
            <Td isNumeric={true}>{count(true, "NON_ROUTINE")}</Td>
          </Tr>
          <Tr>
            <Td pl={0}>Non-concerning</Td>
            <Td isNumeric={true}>{count(false, "ROUTINE")}</Td>
            <Td isNumeric={true}>{count(false, "NON_ROUTINE")}</Td>
          </Tr>
        </Tbody>
      </Table>
      <Text color="gray.600" fontSize="sm" mt={2}>
        {aiQuality.analysedNotes === 0
          ? "No notes analysed in these dates."
          : `Triage closed ${percent(aiQuality.closedByTriage, aiQuality.analysedNotes)} of ${
              aiQuality.analysedNotes
            } analysed notes without the full model.`}
        {aiQuality.auditSampled === 0
          ? ""
          : ` Audit sample: ${aiQuality.auditSampledFlagged} of ${aiQuality.auditSampled} later rated high or urgent.`}
      </Text>
    </Box>
  );
}
